import React, { useState } from 'react';

const ModelExplanations = ({ explanations, modelType, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('summary');

  if (!isOpen || !explanations) return null;

  const summary = explanations.summary || {};
  const featureImportance = explanations.feature_importance || {};
  const shap = explanations.shap || {};
  const lime = explanations.lime || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">🔍 Model Explanations</h2>
              <p className="text-blue-100 mt-1">Understanding your {modelType} model's behavior</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-8 px-6">
            {['summary', 'feature_importance', 'shap', 'lime'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.replace('_', ' ')}
                {tab === 'feature_importance' && featureImportance.data && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    ✓
                  </span>
                )}
                {tab === 'shap' && shap.summary_plot && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    ✓
                  </span>
                )}
                {tab === 'lime' && lime.explanations && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">📊 Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="text-2xl font-bold text-blue-600">{summary.sample_size || 'N/A'}</div>
                    <div className="text-sm text-gray-600">Samples Analyzed</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="text-2xl font-bold text-green-600">
                      {summary.successful_methods?.length || 0}
                    </div>
                    <div className="text-sm text-gray-600">Methods Used</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="text-2xl font-bold text-purple-600">{modelType}</div>
                    <div className="text-sm text-gray-600">Model Type</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">💡 Key Insights</h3>
                {summary.insights && summary.insights.length > 0 ? (
                  <ul className="space-y-2">
                    {summary.insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span className="text-blue-500 font-bold">•</span>
                        <span className="text-gray-700">{insight}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No insights available</p>
                )}
              </div>

              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">🔧 Available Methods</h3>
                <div className="flex flex-wrap gap-2">
                  {summary.available_libraries?.shap && (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                      ✅ SHAP Available
                    </span>
                  )}
                  {summary.available_libraries?.lime && (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                      ✅ LIME Available
                    </span>
                  )}
                  {summary.successful_methods?.map((method) => (
                    <span key={method} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {method.replace('_', ' ').toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Feature Importance Tab */}
          {activeTab === 'feature_importance' && (
            <div className="space-y-6">
              {featureImportance.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-red-800 font-semibold">❌ Error</h3>
                  <p className="text-red-600 mt-2">{featureImportance.error}</p>
                </div>
              ) : (
                <>
                  {featureImportance.plot && (
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4">📊 Feature Importance Plot</h3>
                      <div className="flex justify-center">
                        <img 
                          src={`data:image/png;base64,${featureImportance.plot}`}
                          alt="Feature Importance"
                          className="max-w-full h-auto rounded shadow-lg"
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-2 text-center">
                        Method: {featureImportance.method || 'Unknown'}
                      </p>
                    </div>
                  )}

                  {featureImportance.data && (
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4">📋 Top Features</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Rank</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Feature</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Importance</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Relative</th>
                            </tr>
                          </thead>
                          <tbody>
                            {featureImportance.data.slice(0, 10).map((item, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-4 py-2 text-sm text-gray-900">{idx + 1}</td>
                                <td className="px-4 py-2 text-sm font-medium text-gray-900">{item.feature}</td>
                                <td className="px-4 py-2 text-sm text-gray-700">{item.importance.toFixed(4)}</td>
                                <td className="px-4 py-2">
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full"
                                      style={{
                                        width: `${(item.importance / featureImportance.data[0].importance) * 100}%`
                                      }}
                                    ></div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* SHAP Tab */}
          {activeTab === 'shap' && (
            <div className="space-y-6">
              {shap.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-red-800 font-semibold">❌ SHAP Error</h3>
                  <p className="text-red-600 mt-2">{shap.error}</p>
                </div>
              ) : (
                <>
                  {shap.summary_plot && (
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4">🎯 SHAP Summary Plot</h3>
                      <div className="flex justify-center">
                        <img 
                          src={`data:image/png;base64,${shap.summary_plot}`}
                          alt="SHAP Summary"
                          className="max-w-full h-auto rounded shadow-lg"
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-2 text-center">
                        Sample size: {shap.sample_size || 'Unknown'}
                      </p>
                    </div>
                  )}

                  {shap.average_impact && (
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4">📊 Average SHAP Impact</h3>
                      <div className="space-y-2">
                        {shap.average_impact.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="font-medium">{item.feature}</span>
                            <span className="text-sm text-gray-600">
                              {item.shap_importance.toFixed(4)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* LIME Tab */}
          {activeTab === 'lime' && (
            <div className="space-y-6">
              {lime.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-red-800 font-semibold">❌ LIME Error</h3>
                  <p className="text-red-600 mt-2">{lime.error}</p>
                </div>
              ) : (
                <>
                  {lime.explanations && lime.explanations.length > 0 ? (
                    <div className="space-y-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-800 mb-2">🔍 Individual Predictions</h3>
                        <p className="text-blue-600 text-sm">
                          {lime.summary} ({lime.explanation_type})
                        </p>
                      </div>
                      
                      {lime.explanations.map((exp, idx) => (
                        <div key={idx} className="bg-white border rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-3">
                            Instance #{exp.instance_id + 1}
                          </h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="bg-gray-50 rounded p-3">
                              <div className="text-sm text-gray-600">Prediction</div>
                              <div className="text-lg font-bold text-gray-800">
                                {typeof exp.prediction === 'number' ? exp.prediction.toFixed(4) : exp.prediction}
                              </div>
                            </div>
                            {exp.confidence && (
                              <div className="bg-green-50 rounded p-3">
                                <div className="text-sm text-gray-600">Confidence</div>
                                <div className="text-lg font-bold text-green-600">
                                  {(exp.confidence * 100).toFixed(1)}%
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <h5 className="font-medium text-gray-700">Feature Contributions:</h5>
                            {exp.features.slice(0, 8).map((feat, featIdx) => (
                              <div key={featIdx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex items-center space-x-2">
                                  <span className="font-medium text-sm">{feat.feature}</span>
                                  <span className="text-xs text-gray-500">
                                    (value: {feat.value.toFixed(3)})
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className={`text-sm font-medium ${
                                    feat.weight > 0 ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {feat.weight > 0 ? '+' : ''}{feat.weight.toFixed(3)}
                                  </span>
                                  <div className="w-16 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${
                                        feat.weight > 0 ? 'bg-green-500' : 'bg-red-500'
                                      }`}
                                      style={{
                                        width: `${Math.abs(feat.weight) * 100}%`
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <p className="text-gray-500">No LIME explanations available</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Generated explanations for {modelType} model
            </div>
            <button
              onClick={onClose}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelExplanations;